import { useCombobox } from 'downshift';
import debounce from 'lodash.debounce';
import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEvent,
  MouseEventHandler,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import useGetSuggestionHref from './useGetSuggestionHref';
import useSearchBarEvents, { EventData } from './useSearchBarEvents';
import useSuggestions from './useSuggestions';
import { noSelectedSuggestion, SearchType } from '../constants';
import { useInputContext } from '../context/InputContext';
import type { Suggestion, SuggestionItem } from '../contracts';
import DefaultSuggestionClicked from '../contracts/DefaultSuggestionClicked';
import { normalizeSuggestionSelection } from '../helpers';
import SearchBarContext from '../SearchBarContext';
import {
  removeSuggestionFromLocalStorage,
  saveSuggestionToLocalStorage
} from '../suggestions';
import { useSuggestionsContext } from '../SuggestionsContext';

export default function useSearchBar() {
  const {
    inputValue,
    inputRef,
    setInputValue,
    setSuggestionQuery,
    suggestionQuery,
    noResults
  } = useInputContext();
  const {
    customEventData,
    id,
    onSuggestionSelection,
    trackEvent,
    onCloseModal
  } = useContext(SearchBarContext);
  const {
    suggestions: {
      data: suggestedSearches,
      isLoading: suggestedSearchesLoading,
      setLoading: setSuggestionsLoading
    }
  } = useSuggestionsContext();
  const getSuggestionUrl = useGetSuggestionHref();
  const searchBarEvents = useSearchBarEvents(trackEvent);
  const [pressedEnter, setPressedEnter] = useState(false);
  const { setRecentSearches, setKeyPressed, suggestionResults } =
    useSuggestions();
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const { closeMenu, isOpen, getMenuProps, getInputProps, getItemProps } =
    useCombobox({
      id: `merch-search-bar${id ? `-${id}` : ''}`,
      items: suggestionResults
    });

  const hasInput = inputValue.length > 0;
  const hasSuggestionQuery = suggestionQuery.length > 0;
  const highlightedSuggestion = suggestionResults[highlightedIndex];
  const suggestedSearchesLength = suggestedSearches.length;

  const getSuggestionEventData = useCallback(
    (suggestion: SuggestionItem): EventData => ({
      ...customEventData,
      buildKey: 'buildKey' in suggestion ? suggestion.buildKey : null,
      suggestionIndex:
        'suggestionIndex' in suggestion ? suggestion.suggestionIndex : null,
      suggestionsLength: suggestedSearchesLength,
      suggestionText: suggestion.text,
      textEntered: inputValue,
      textSource: 'searchType' in suggestion ? suggestion.searchType : null
    }),
    [customEventData, inputValue, suggestedSearchesLength]
  );

  const handleResetMenu = useCallback(
    (text: string) => {
      // blur input on close to prevent browser scrolling input in view
      inputRef.current?.blur();
      closeMenu();
      setHighlightedIndex(-1);
      setInputValue(text);

      // Close modal async to avoid any default scroll events while the modal is closing on iOS
      setTimeout(() => onCloseModal?.(), 0);
    },
    [closeMenu, inputRef, onCloseModal, setInputValue, setHighlightedIndex]
  );

  const handleDefaultSuggestionClicked: DefaultSuggestionClicked = useCallback(
    (e, suggestionText) => {
      handleResetMenu(suggestionText);
    },
    [handleResetMenu]
  );

  const handleSuggestionSelection = useCallback(
    (suggestion: Suggestion, eventData: EventData) => {
      const { href, searchHash, text } = suggestion;

      const suggestionUrl = href ? getSuggestionUrl(href, eventData) : '';

      handleResetMenu(text);

      if (onSuggestionSelection) {
        onSuggestionSelection({
          cvnaId: searchHash ?? '',
          url: suggestionUrl,
          eventData
        });

        return;
      }

      if (suggestionUrl) {
        window.location.href = suggestionUrl;
      }
    },
    [getSuggestionUrl, handleResetMenu, onSuggestionSelection]
  );

  const handleInputOnFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    e => {
      e.preventDefault();
      setSuggestionQuery(e.target.value);
      searchBarEvents.focused();
    },
    [searchBarEvents, setSuggestionQuery]
  );

  const debouncedSetSuggestionQuery = useMemo(
    () => debounce(setSuggestionQuery, 250),
    [setSuggestionQuery]
  );

  const handleInputOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    async e => {
      const newInputValue = e.currentTarget.value;
      setSuggestionsLoading();
      setInputValue(newInputValue);
      debouncedSetSuggestionQuery(newInputValue);
    },
    [debouncedSetSuggestionQuery, setInputValue, setSuggestionsLoading]
  );

  const handleClearText = useCallback(() => {
    setInputValue('');
    setSuggestionQuery('');
  }, [setInputValue, setSuggestionQuery]);

  const handleRemoveRecentSearch: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      e => {
        e.preventDefault();
        e.stopPropagation();
        searchBarEvents.removeRecentSearch();

        const { value } = e.currentTarget;

        if (e.currentTarget?.value) {
          setRecentSearches(
            removeSuggestionFromLocalStorage(value, inputValue)
          );
        }
      },
      [inputValue, searchBarEvents, setRecentSearches]
    );

  const handleGoButtonClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();

      const suggestion = highlightedSuggestion || noSelectedSuggestion;

      // Save Suggested Search to Local Storage
      if (suggestion.searchType === SearchType.TextSearch) {
        setRecentSearches(saveSuggestionToLocalStorage(suggestion));
      }

      const eventData = getSuggestionEventData(suggestion);

      searchBarEvents.goClicked(eventData);
      handleSuggestionSelection(
        normalizeSuggestionSelection(suggestion),
        eventData
      );
    },
    [
      getSuggestionEventData,
      handleSuggestionSelection,
      highlightedSuggestion,
      searchBarEvents,
      setRecentSearches
    ]
  );

  const handleSuggestionClick: MouseEventHandler = useCallback(
    e => {
      e.preventDefault();

      if (!highlightedSuggestion) return;

      const suggestion = highlightedSuggestion;

      // Save Suggested Search to Local Storage
      if (suggestion.searchType === SearchType.TextSearch) {
        setRecentSearches(saveSuggestionToLocalStorage(suggestion));
      }

      const eventData = getSuggestionEventData(suggestion);

      searchBarEvents.suggestionClicked(eventData);
      handleSuggestionSelection(
        normalizeSuggestionSelection(suggestion),
        eventData
      );
    },
    [
      getSuggestionEventData,
      handleSuggestionSelection,
      highlightedSuggestion,
      searchBarEvents,
      setRecentSearches
    ]
  );

  useEffect(() => {
    if (noResults) {
      setHighlightedIndex(-1);
    }
  }, [noResults]);

  useEffect(() => {
    if (suggestionQuery) {
      setHighlightedIndex(prevIndex => (prevIndex > -1 ? prevIndex : 0));
    } else {
      setHighlightedIndex(-1);
    }
  }, [suggestionQuery]);

  useEffect(() => {
    if (suggestedSearchesLoading || !pressedEnter) return;

    const suggestion = highlightedSuggestion || noSelectedSuggestion;

    // Save Suggested Search to Local Storage
    if (suggestion.searchType === SearchType.TextSearch) {
      setRecentSearches(saveSuggestionToLocalStorage(suggestion));
    }

    const eventData = getSuggestionEventData(suggestion);

    searchBarEvents.pressedEnter(eventData);
    handleSuggestionSelection(
      normalizeSuggestionSelection(suggestion),
      eventData
    );

    setPressedEnter(false);
  }, [
    getSuggestionEventData,
    handleSuggestionSelection,
    highlightedSuggestion,
    pressedEnter,
    searchBarEvents,
    setRecentSearches,
    suggestedSearchesLoading
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      let currentSelection;
      setKeyPressed(e.key);

      switch (e.key) {
        case 'Backspace':
          if (inputValue.length <= 1) {
            return;
          }
          setPressedEnter(false);
          break;

        case 'Enter':
          setPressedEnter(true);

          return;

        case 'ArrowUp':
          if (highlightedIndex === -1) return;
          currentSelection = highlightedIndex - 1;
          setHighlightedIndex(currentSelection);
          if (suggestionResults[currentSelection]?.text) {
            setInputValue(suggestionResults[currentSelection].text);
          } else {
            setInputValue(suggestionQuery);
          }
          break;

        case 'ArrowDown':
          currentSelection = highlightedIndex + 1;
          if (currentSelection === suggestionResults.length) {
            return;
          }
          setHighlightedIndex(currentSelection);
          if (suggestionResults[currentSelection]?.text) {
            setInputValue(suggestionResults[currentSelection].text);
          } else {
            setInputValue(suggestionQuery);
          }
          break;

        default:
          break;
      }
    },
    [
      inputValue.length,
      highlightedIndex,
      setInputValue,
      setKeyPressed,
      suggestionQuery,
      suggestionResults
    ]
  );

  return useMemo(
    () => ({
      getInputProps,
      getItemProps,
      getMenuProps,
      handleClearText,
      handleDefaultSuggestionClicked,
      handleInputOnChange,
      handleInputOnFocus,
      handleKeyDown,
      handleGoButtonClick,
      handleRemoveRecentSearch,
      handleSuggestionClick,
      hasInput,
      hasSuggestionQuery,
      highlightedIndex,
      isOpen,
      setHighlightedIndex,
      suggestionResults
    }),
    [
      getInputProps,
      getItemProps,
      getMenuProps,
      handleClearText,
      handleDefaultSuggestionClicked,
      handleGoButtonClick,
      handleInputOnChange,
      handleInputOnFocus,
      handleKeyDown,
      handleRemoveRecentSearch,
      handleSuggestionClick,
      hasInput,
      hasSuggestionQuery,
      highlightedIndex,
      isOpen,
      suggestionResults
    ]
  );
}
